import type { Tag } from '../shared/contract.js';
import type { HookCtx, Modifier, OddsCtx } from './modifier.js';
import { hasTag } from './token.js';
import { norm, scale, sub, tuple, type Vec3 } from './vec.js';

/**
 * TAG PHYSICAL DEFAULTS — the free half of the two-level resolution.
 *
 * These are ordinary Modifiers, registered in the pipeline for every run. That is the
 * point: a tag's physical meaning is not machine code, it is a subscriber to the same
 * hooks every item uses. A magnet token curves toward its neighbours in PACHINKO, clumps
 * the MÜNZSCHIEBER pile and drags a follower swarm — and no machine module knows.
 *
 * Machines only ever add *declared odds mappings* on top (their tag interpretation
 * tables, which live in the machine specs as data).
 */

/** How far a single token may ripen past its base value. See [Zins] below. */
export const ZINS_CEILING = 512;

const TUNING = {
  magnetRadius: 3.5,
  /**
   * Measured, not guessed: a coin resting in the pile is held by roughly 4.7 N of
   * static friction (µ≈0.48 against the shelf, plus the coins stacked on it). A pull of
   * 7 N/(1+d²) is under 3.5 N at one radius out, so the old value looked like a magnet
   * and did nothing. 26 N/(1+d²) is ~13 N adjacent — it genuinely clumps a pile.
   */
  magnetForce: 26,
  /** the magnet is dragged back too, but gently: a dozen neighbours must not launch it */
  magnetSelfShare: 0.08,
  /** work cap per tick; with nearest-first ordering these are the 24 closest coins */
  magnetMaxNeighbours: 16,
  gummiFreeBounces: 3,
  gummiRestitution: 0.99,
  gummiTiredRestitution: 0.82,
  schwerMassMult: 3,
  zinsPeriod: 10,
  zinsMult: 2,
  echoDelay: 3,
  echoShare: 0.5,
  gluecksMouthMult: 1.2,
  goldMult: 5,
  feuerImpulse: 1.4,
} as const;

const gate = (ctx: HookCtx, tag: Tag): boolean => ctx.tags.indexOf(tag) >= 0;

export function tagDefaults(): Modifier[] {
  const mods: Modifier[] = [];

  // ---------------------------------------------------------------- [Schwer]
  mods.push({
    id: 'tag:Schwer',
    source: 'enchant',
    tags: ['Schwer'],
    priority: 90,
    ruleText: 'Schwere Marken wiegen das Dreifache — sie pflügen durch Stifte und sinken in Haufen.',
    queries: {
      mass: (ctx, v) => (gate(ctx, 'Schwer') ? v * TUNING.schwerMassMult : v),
    },
  });

  // ---------------------------------------------------------------- [Gummi]
  mods.push({
    id: 'tag:Gummi',
    source: 'enchant',
    tags: ['Gummi'],
    priority: 91,
    ruleText: 'Gummigeld springt die ersten drei Stöße verlustfrei.',
    queries: {
      restitution: (ctx, v) => {
        if (!gate(ctx, 'Gummi')) return v;
        const t = ctx.token;
        if (t && t.bounces >= TUNING.gummiFreeBounces) return TUNING.gummiTiredRestitution;
        return TUNING.gummiRestitution;
      },
    },
    events: {
      onCollision: (ctx) => {
        if (!gate(ctx, 'Gummi') || !ctx.token) return;
        ctx.token.bounces++;
      },
    },
  });

  // ---------------------------------------------------------------- [Gold]
  mods.push({
    id: 'tag:Gold',
    source: 'enchant',
    tags: ['Gold'],
    priority: 92,
    ruleText: 'Vergoldete Marken zählen in der Materialstufe als Gold.',
    queries: {
      materialMult: (ctx, v) => (gate(ctx, 'Gold') ? Math.max(v, TUNING.goldMult) : v),
    },
  });

  // ---------------------------------------------------------------- [Fluch]
  mods.push({
    id: 'tag:Fluch',
    source: 'enchant',
    tags: ['Fluch'],
    priority: 93,
    ruleText:
      'Verfluchte Marken würfeln verkehrt herum: Jackpot- und Nietenchance tauschen — zahlen aber selbst nichts aus.',
    queries: {
      // the whole tank build lives on this one line
      odds: (ctx: OddsCtx, p: number) => (gate(ctx, 'Fluch') ? 1 - p : p),
      // ...and its price is the other one. economy.md prices cursed material at ×0:
      // a curse token cannot cash in, so the only way to spend a curse build is to
      // farm GLÜCK off its losses and steer someone else's roll. That is the design.
      materialMult: (ctx, v) => (gate(ctx, 'Fluch') ? 0 : v),
    },
  });

  // ---------------------------------------------------------------- [Gluecks]
  mods.push({
    id: 'tag:Gluecks',
    source: 'enchant',
    tags: ['Gluecks'],
    priority: 94,
    ruleText: 'Glücksmarken sehen jede Öffnung 20 % breiter.',
    queries: {
      pocketWidth: (ctx, v) => (gate(ctx, 'Gluecks') ? v * TUNING.gluecksMouthMult : v),
    },
  });

  // ---------------------------------------------------------------- [Magnet]
  mods.push({
    id: 'tag:Magnet',
    source: 'enchant',
    tags: ['Magnet'],
    priority: 100,
    ruleText: 'Magnetmarken ziehen andere Marken an — und werden selbst leicht mitgezogen.',
    events: {
      onTokenTick: (ctx, { dt }) => {
        if (!gate(ctx, 'Magnet') || !ctx.token) return;
        const self = ctx.token;
        const api = ctx.api;
        const here = api.position(self.id);
        if (!here) return;
        const near = api.neighbours(self.id, TUNING.magnetRadius);
        const n = Math.min(near.length, TUNING.magnetMaxNeighbours);
        for (let i = 0; i < n; i++) {
          const other = near[i]!;
          const there = api.position(other);
          if (!there) continue;
          const d = sub(here, there);
          const dd = d.x * d.x + d.y * d.y + d.z * d.z;
          if (dd < 1e-6) continue;
          const dir = norm(d);
          const f = (TUNING.magnetForce * self.potency * dt) / (1 + dd);
          api.impulse(other, scale(dir, f));
          api.impulse(self.id, scale(dir, -f * TUNING.magnetSelfShare));
        }
      },
    },
  });

  // ---------------------------------------------------------------- [Geist]
  mods.push({
    id: 'tag:Geist',
    source: 'enchant',
    tags: ['Geist'],
    priority: 101,
    ruleText: 'Geistermünzen fallen durch den ersten Körper, den sie treffen — und liegen dann darin.',
    events: {
      onTokenTick: (ctx) => {
        if (!gate(ctx, 'Geist') || !ctx.token) return;
        const t = ctx.token;
        const api = ctx.api;
        if (t.ghost === 'spent') return;

        const overlap = api.solidOverlap(t.id);
        if (t.ghost === 'armed') {
          api.setGhost(t.id, true);
          if (overlap !== null) {
            t.ghost = 'phasing';
            api.emit({ t: 'phase', token: t.id });
          }
          return;
        }
        // 'phasing': stay immaterial until the token finds free space, or until it has
        // come to rest. In PACHINKO that means it slips the peg it hit and re-solidifies
        // in the gap beyond. In the MÜNZSCHIEBER it means it sinks through the surface
        // of the pile and lands solid in the INTERIOR — deep-core mining, which is the
        // whole point of the combo. (Ending the phase on "cleared that one specific
        // body" instead left it sitting on top of the pile, phasing nothing.)
        if (overlap === null || t.idleSeconds > 0.12) {
          t.ghost = 'spent';
          api.setGhost(t.id, false);
        }
      },
    },
  });

  // ---------------------------------------------------------------- [Kleber]
  mods.push({
    id: 'tag:Kleber',
    source: 'enchant',
    tags: ['Kleber'],
    priority: 102,
    ruleText: 'Klebriges Pech bleibt liegen, wo es landet, und wird selbst zum Gelände.',
    events: {
      onCollision: (ctx, p) => {
        if (!gate(ctx, 'Kleber') || !ctx.token) return;
        const t = ctx.token;
        if (t.stuck) return;
        // stick to pegs/ledges/shelf, or to something already stuck (dams grow
        // themselves). NOT to cabinet walls or the launch rail — a token welded into
        // the launcher jams the machine for the rest of the session.
        // ...and to anything firmly settled: an already-stuck token, or packed pile
        // sediment. Not to a coin still in flight, which would leave dams hanging in
        // mid-air, and not to the cabinet shell.
        const other = p.b === null ? null : ctx.api.token(p.b);
        const anchor = p.anchor || (other !== null && (other.stuck || other.sedimented));
        if (!anchor) return;
        ctx.api.freeze(t.id);
        const at = ctx.api.position(t.id);
        ctx.api.emit({ t: 'stick', token: t.id, at: at ? tuple(at) : [0, 0, 0] });
      },
    },
  });

  // ---------------------------------------------------------------- [Feuer]
  mods.push({
    id: 'tag:Feuer',
    source: 'enchant',
    tags: ['Feuer'],
    priority: 103,
    ruleText: 'Feuermarken stoßen alles weg, was sie berühren.',
    events: {
      onCollision: (ctx, p) => {
        if (!gate(ctx, 'Feuer') || !ctx.token || p.b === null) return;
        const here = ctx.api.position(ctx.token.id);
        const there = ctx.api.position(p.b);
        if (!here || !there) return;
        const dir: Vec3 = norm(sub(there, here));
        ctx.api.impulse(p.b, scale(dir, TUNING.feuerImpulse));
      },
    },
  });

  // ---------------------------------------------------------------- [Zins]
  mods.push({
    id: 'tag:Zins',
    source: 'enchant',
    tags: ['Zins'],
    priority: 110,
    ruleText: 'Zinsmarken verdoppeln ihren Wert je 10 Sekunden im Automaten.',
    events: {
      onTokenTick: (ctx, { dt }) => {
        if (!gate(ctx, 'Zins') || !ctx.token) return;
        const t = ctx.token;
        t.zinsClock += dt;
        while (t.zinsClock >= TUNING.zinsPeriod) {
          t.zinsClock -= TUNING.zinsPeriod;
          // DECISION: ripening is capped at ZINS_CEILING x the token's base value. A coin
          // left in the pusher for fourteen minutes was reaching 1e15 and the run's whole
          // economy stopped meaning anything. The ceiling is high enough that no ordinary
          // session touches it (about fifteen ripenings) and low enough that "leave it in
          // and go and do something else" is a strategy rather than a win button.
          if (t.faceValue >= t.baseValue * ZINS_CEILING) break;
          t.faceValue = t.faceValue * TUNING.zinsMult;
          ctx.api.emit({ t: 'zinsTick', token: t.id, newValue: t.faceValue });
        }
      },
    },
  });

  // ---------------------------------------------------------------- [Spalt]
  mods.push({
    id: 'tag:Spalt',
    source: 'enchant',
    tags: ['Spalt'],
    priority: 120,
    ruleText: 'Spaltmarken zerspringen bei Jackpot-Treffern in zwei halbe Marken.',
    events: {
      onScore: (ctx, p) => {
        if (!gate(ctx, 'Spalt') || !ctx.token) return;
        if (!p.jackpot) return;
        ctx.api.splitToken(ctx.token.id);
        /**
         * The board owes an explanation for what just happened on screen.
         *
         * `Pipeline.query` records provenance automatically whenever a modifier changes
         * an answer, which is why every *query*-side rule prints itself. `Pipeline.fire`
         * cannot do the same — an event hook has no before/after value to compare — so an
         * event-side rule has to say so itself, and this one never did. The result was
         * that the most spectacular thing a tag does in this game, a coin coming apart in
         * mid-air, was the one rule the Hausregeln board could not attribute. test/
         * pipeline.test.ts already states the principle for queries ("a modifier that
         * changes an answer owes the board an entry"); this is the same principle on the
         * other side of the pipeline.
         */
        ctx.note({
          sourceId: 'tag:Spalt',
          source: 'enchant',
          text: 'Spaltmarken zerspringen bei Jackpot-Treffern in zwei halbe Marken.',
          discovered: true,
        });
      },
    },
  });

  // ---------------------------------------------------------------- [Echo]
  mods.push({
    id: 'tag:Echo',
    source: 'enchant',
    tags: ['Echo'],
    priority: 130,
    ruleText: 'Echomarken wiederholen ihren letzten Treffer 3 Sekunden später zum halben Wert.',
    events: {
      onScore: (ctx, p) => {
        if (!gate(ctx, 'Echo') || !ctx.token) return;
        if (p.pocket.startsWith('echo:')) return; // an echo never echoes itself
        ctx.api.scheduleEcho(ctx.token.id, p.value * TUNING.echoShare, TUNING.echoDelay);
      },
    },
  });

  return mods;
}

/** Every tag the grammar knows, in a fixed order (used by lints and help cards). */
export const ALL_TAGS: readonly Tag[] = [
  'Magnet', 'Geist', 'Gummi', 'Kleber', 'Spalt', 'Gold',
  'Fluch', 'Feuer', 'Gluecks', 'Schwer', 'Zins', 'Echo',
];

export { hasTag };
